import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dialog, DialogContent, DialogTitle, DialogActions, Box, FormControl } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ScheduleSection } from "pages/CreateSchedulerRule/components/ScheduleSection/ScheduleSection";
import {
    emailExceptionsSchema,
    exceptionDefaultValues,
    SchedulerExceptionForm,
} from "validation/components/EmailScheduler/exceptionsFormRules";
import { useProcessingStatus } from "hooks";
import { fetchSchedulerRuleExceptions } from "store/features/emailScheduler/exceptions/exceptions.actions";
import {
    getEmailSchedulerExceptionsState,
    getExceptionEditProperties,
} from "store/features/emailScheduler/exceptions/exceptions.selectors";
import {
    getEmailSchedulerDefinitionProcessingState,
    getExceptionById,
} from "store/features/emailScheduler/definition/definition.selectors";
import { fetchSchedulerDefinitions } from "store/features/emailScheduler/definition/definition.actions";
import { FilterSection } from "components/FilterSection/FilterSection";
import { getFiltersFields } from "store/features/filters";
import { useSubmitStatus } from "hooks/useSubmitStatus";
import { TextField } from "components";

interface CreateConfigDialog {
    isOpen: boolean;
    onToggle(isOpen: boolean): () => void;
    onSubmit(formData: SchedulerExceptionForm): void;
}

export const ExceptionDialog: React.FC<CreateConfigDialog> = ({ isOpen, onToggle, onSubmit }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const definitions = useSelector(getFiltersFields);
    const exceptionsProcessingState = useSelector(getEmailSchedulerExceptionsState);
    const definitionsProcessingState = useSelector(getEmailSchedulerDefinitionProcessingState);
    const { isEdited, id } = useSelector(getExceptionEditProperties);
    const exceptionDetails = useSelector(getExceptionById(id));
    useProcessingStatus(
        definitionsProcessingState.isProcessing || exceptionsProcessingState.isProcessing,
        definitionsProcessingState.hasError || exceptionsProcessingState.hasError
    );
    useSubmitStatus(
        { isSubmitted: exceptionsProcessingState.isSubmitted, message: "Exceptions saved successfully" },
        () => {
            onToggle(false)();
            dispatch(fetchSchedulerRuleExceptions.request());
        }
    );

    const methods = useForm<SchedulerExceptionForm>({
        resolver: yupResolver(emailExceptionsSchema),
        defaultValues: exceptionDefaultValues,
    });

    useEffect(() => {
        if (!definitions.length && isOpen) {
            dispatch(fetchSchedulerDefinitions.request());
        }
    }, [isOpen, definitions.length, dispatch]);

    useEffect(() => {
        if (isEdited) {
            methods.reset(exceptionDetails);
        }
        // eslint-disable-next-line
    }, [isEdited, id, exceptionDetails]);

    const handleFormReset = () => methods.reset(exceptionDefaultValues);

    return (
        <Dialog open={isOpen} onClose={onToggle(false)} fullWidth={true} maxWidth={"lg"} onExited={handleFormReset}>
            <DialogTitle id="form-dialog-title">{isEdited ? "Edit exception" : "Create new exception"}</DialogTitle>
            <DialogContent dividers={false}>
                <FormProvider {...methods}>
                    <form>
                        <Box display="flex" alignItems="center" pb={2}>
                            <Box flex={0.5}>
                                <FormControl fullWidth={true}>
                                    <Controller
                                        name="name"
                                        control={methods.control}
                                        as={
                                            <TextField
                                                disabled={isEdited}
                                                label="Exception name"
                                                error={!!methods.errors.name}
                                                helperText={methods.errors.name?.message}
                                            />
                                        }
                                    />
                                </FormControl>
                            </Box>
                        </Box>

                        <FilterSection source="notification/autocomplete" />
                        <ScheduleSection />
                    </form>
                </FormProvider>
            </DialogContent>
            <DialogActions>
                <Button disableRipple onClick={onToggle(false)} color="primary">
                    {t("buttons.label.cancel")}
                </Button>
                <Button
                    disableRipple
                    onClick={methods.handleSubmit(onSubmit)}
                    disabled={exceptionsProcessingState.isProcessing}
                    color="primary">
                    {t("buttons.label.submit")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
